import { useState } from 'react';
import { X, Bell, Car, MessageCircle, Check, XCircle } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Notification, NotificationType, UserRole } from '@/types';
import { notificationsApi, ridesApi, getApiErrorMessage, RideRequestAction } from '@/lib/api';

interface NotificationsPanelProps {
  isOpen: boolean;
  onClose: () => void;
  currentUserRole?: UserRole;
  onViewRideRequests?: (rideId: string) => void;
  onViewRides?: () => void;
  onViewConversation?: (conversationId: string) => void;
}

export const NotificationsPanel = ({ 
  isOpen, 
  onClose,
  currentUserRole,
  onViewRideRequests,
  onViewRides,
  onViewConversation
}: NotificationsPanelProps) => {
  const queryClient = useQueryClient();
  const [activeRequestId, setActiveRequestId] = useState<string | null>(null);
  
  const { data: notifications = [], isLoading } = useQuery({
    queryKey: ['notifications'],
    queryFn: notificationsApi.getNotifications,
    enabled: isOpen,
    refetchInterval: 10000, // Refresh every 10 seconds to get new notifications
  });

  const markAsReadMutation = useMutation({
    mutationFn: notificationsApi.markAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });

  const markAllAsReadMutation = useMutation({
    mutationFn: notificationsApi.markAllAsRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      toast.success('All notifications marked as read');
    },
  });

  const respondToRequestMutation = useMutation({
    mutationFn: async ({ rideId, requestId, action }: { rideId: string; requestId: string; action: RideRequestAction }) => {
      setActiveRequestId(requestId);
      return ridesApi.respondToRequest(rideId, requestId, action);
    },
    onSuccess: (_, variables) => {
      toast.success(variables.action === 'accept' ? 'Seat request accepted.' : 'Seat request declined.');
      queryClient.invalidateQueries({ queryKey: ['myRides'] });
      queryClient.invalidateQueries({ queryKey: ['myRequests'] });
      queryClient.invalidateQueries({ queryKey: ['availableRides'] });
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to update ride request'));
    },
    onSettled: () => {
      setActiveRequestId(null);
    },
  });

  const handleRequestAction = (notification: Notification, action: RideRequestAction) => {
    const rideId = notification.data?.rideId;
    const requestId = notification.data?.requestId;
    if (!rideId || !requestId || respondToRequestMutation.isPending) return;

    if (!notification.read) {
      markAsReadMutation.mutate(notification.id);
    }

    respondToRequestMutation.mutate({ rideId, requestId, action });
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsReadMutation.mutate(notification.id);
    }
    
    // Close the notifications panel first
    onClose();
    
    // Handle based on notification type and call appropriate callback
    switch (notification.type) {
      case 'ride_request':
        // Get rideId from notification data and open the requests panel
        if (notification.data?.rideId && onViewRideRequests) {
          onViewRideRequests(notification.data.rideId);
        } else if (onViewRideRequests) {
          // If no specific rideId, show all requests
          onViewRideRequests('all');
        }
        break;
      case 'request_accepted':
      case 'request_rejected':
        // Navigate to rides (show the rides tab)
        if (onViewRides) {
          onViewRides();
        }
        break;
      case 'message':
        // Open the conversation
        if (notification.data?.conversationId && onViewConversation) {
          onViewConversation(notification.data.conversationId);
        }
        break;
      default:
        break;
    }
  };

  const getIcon = (type: NotificationType) => {
    switch (type) {
      case 'ride_request':
      case 'request_accepted':
      case 'request_rejected':
        return <Car className="w-5 h-5 text-primary" />;
      case 'message':
        return <MessageCircle className="w-5 h-5 text-accent" />;
      default:
        return <Bell className="w-5 h-5 text-muted-foreground" />;
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center sm:p-4">
      <div className="bg-background w-full sm:w-[400px] sm:rounded-2xl rounded-t-2xl shadow-xl flex flex-col h-[80vh] sm:h-[600px]">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Bell className="w-6 h-6 text-primary" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </div>
            <div>
              <h3 className="font-bold text-lg">Notifications</h3>
              <p className="text-xs text-muted-foreground">
                {unreadCount} unread
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <button
                onClick={() => markAllAsReadMutation.mutate()}
                className="text-xs text-primary hover:underline"
                disabled={markAllAsReadMutation.isPending}
              >
                Mark all read
              </button>
            )}
            <button 
              onClick={onClose} 
              className="p-2 hover:bg-muted rounded-full"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-12">
              <Bell className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No notifications yet</p>
              <p className="text-sm text-muted-foreground/60">
                We'll notify you about ride updates and messages
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`w-full p-4 flex items-start gap-3 hover:bg-muted/50 transition-colors text-left ${
                    !notification.read ? 'bg-primary/5' : ''
                  }`}
                >
                  <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center shrink-0">
                    {getIcon(notification.type)}
                  </div>
                  <button
                    onClick={() => handleNotificationClick(notification)}
                    className="flex-1 min-w-0 text-left"
                  >
                    <div className="flex items-start justify-between gap-2">
                      <p className={`font-medium text-sm ${!notification.read ? 'text-foreground' : 'text-muted-foreground'}`}>
                        {notification.title}
                      </p>
                      {!notification.read && (
                        <span className="w-2 h-2 bg-primary rounded-full shrink-0 mt-1.5" />
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                      {notification.message}
                    </p>
                    <p className="text-xs text-muted-foreground/60 mt-2">
                      {formatTimeAgo(notification.createdAt)}
                    </p>
                  </button>
                  {currentUserRole === 'driver' && notification.type === 'ride_request' && notification.data?.rideId && notification.data?.requestId && (
                    <div className="flex flex-col gap-2 shrink-0">
                      <button
                        onClick={() => handleRequestAction(notification, 'accept')}
                        disabled={respondToRequestMutation.isPending && activeRequestId === notification.data?.requestId}
                        className="inline-flex items-center justify-center rounded-md bg-green-600 px-2 py-1 text-xs font-medium text-white hover:bg-green-700 disabled:opacity-50"
                      >
                        <Check className="mr-1 h-3 w-3" />
                        Accept
                      </button>
                      <button
                        onClick={() => handleRequestAction(notification, 'decline')}
                        disabled={respondToRequestMutation.isPending && activeRequestId === notification.data?.requestId}
                        className="inline-flex items-center justify-center rounded-md border border-red-200 px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                      >
                        <XCircle className="mr-1 h-3 w-3" />
                        Decline
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function formatTimeAgo(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (seconds < 60) return 'Just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  
  return date.toLocaleDateString();
}

export default NotificationsPanel;
